import { ReactNode, useEffect, useRef, useState } from "react";

import classNames from "../../utils/classNames";

interface CollapsibleSectionProps {
  isOpen: boolean;
  children: ReactNode;
  className?: string;
  durationMs?: number;
}

export default function CollapsibleSection({
  isOpen,
  children,
  className = "",
  durationMs = 300,
}: CollapsibleSectionProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState("0px");
  // Tracks whether the open transition has finished, so we can stop clipping
  // overflow and allow popovers (date pickers, dropdowns) to escape.
  const [fullyOpen, setFullyOpen] = useState(false);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    if (isOpen) {
      setMaxHeight(`${content.scrollHeight}px`);
      return;
    }

    setFullyOpen(false);
    // Set current height first so collapse animates from actual content size.
    setMaxHeight(`${content.scrollHeight}px`);
    requestAnimationFrame(() => setMaxHeight("0px"));
  }, [isOpen, children]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content || !isOpen) return;

    const observer = new ResizeObserver(() => {
      if (!fullyOpen) {
        setMaxHeight(`${content.scrollHeight}px`);
      }
    });

    observer.observe(content);
    return () => observer.disconnect();
  }, [isOpen, fullyOpen]);

  const handleTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName !== "max-height") return;
    if (isOpen) setFullyOpen(true);
  };

  return (
    <div
      onTransitionEnd={handleTransitionEnd}
      className={classNames(
        "transition-[max-height,opacity] ease-in-out",
        fullyOpen ? "overflow-visible" : "overflow-hidden",
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
        className
      )}
      style={{
        maxHeight: fullyOpen ? "none" : maxHeight,
        transitionDuration: `${durationMs}ms`,
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
}
